import { useGbpHealthQuery } from '../api/queries';
import type { GbpHealthActionItem } from '../types';

type Props = { af: string };

function scoreColor(score: number): string {
  if (score >= 80) return '#15803d';
  if (score >= 65) return '#65a30d';
  if (score >= 50) return '#ca8a04';
  if (score >= 30) return '#ea580c';
  return '#dc2626';
}
function scoreLabel(score: number): string {
  if (score >= 80) return 'Strong';
  if (score >= 65) return 'Good';
  if (score >= 50) return 'Needs work';
  if (score >= 30) return 'Weak';
  return 'Critical';
}

export default function GbpHealthPanel({ af }: Props) {
  const { data, isLoading, error } = useGbpHealthQuery(af);

  if (isLoading) {
    return (
      <div className="ds-panel" style={{ padding: 20, margin: '16px 20px' }}>
        <div style={{ color: '#8a95a5', fontSize: 13 }}>Loading GBP health…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="ds-panel" style={{ padding: 20, margin: '16px 20px' }}>
        <div style={{ color: '#dc2626', fontSize: 13 }}>
          Failed to load: {(error as Error).message}
        </div>
      </div>
    );
  }
  if (!data || data.refreshing) {
    return (
      <div className="ds-panel" style={{ padding: 20, margin: '16px 20px' }}>
        <div style={{ color: '#8a95a5', fontSize: 13 }}>
          {data?.refreshing
            ? 'Refreshing GBP signals in the background — check back in a minute…'
            : 'GBP health not computed yet.'}
        </div>
      </div>
    );
  }

  const high = data.actionItems.filter((a) => a.priority === 'high');
  const medium = data.actionItems.filter((a) => a.priority === 'medium');
  const lowOrNone = data.actionItems.filter((a) => a.priority === 'low');

  return (
    <div style={{ padding: '8px 20px 20px' }}>
      <div className="ds-section-header">
        <h2>GBP Health Score</h2>
        <span className="meta">
          {data.displayName ?? '(GBP listing not found)'}
          {data.businessStatus && data.businessStatus !== 'OPERATIONAL'
            ? ` · ${data.businessStatus}`
            : ''}
        </span>
      </div>

      <div
        className="ds-panel"
        style={{
          padding: 20,
          display: 'grid',
          gridTemplateColumns: 'minmax(220px, 280px) 1fr',
          gap: 24,
          alignItems: 'stretch',
        }}
      >
        <ScoreGauge score={data.score} />
        <Signals data={data} />
      </div>

      {data.actionItems.length > 0 ? (
        <>
          <div className="ds-section-header" style={{ marginTop: 24 }}>
            <h2>Action Items</h2>
            <span className="meta">
              {high.length} high · {medium.length} medium · {lowOrNone.length} low
            </span>
          </div>
          <div className="ds-panel" style={{ padding: 0 }}>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {[...high, ...medium, ...lowOrNone].map((item, i) => (
                <ActionRow key={i} item={item} />
              ))}
            </ul>
          </div>
        </>
      ) : (
        <div
          className="ds-panel"
          style={{
            marginTop: 16,
            padding: 24,
            textAlign: 'center',
            color: '#15803d',
            fontSize: 14,
          }}
        >
          ✓ Your GBP profile is in excellent shape — nothing actionable from our scan.
        </div>
      )}

      <div className="metrics-footer">
        GBP signals via Google Places API · Refreshed weekly · Last sync{' '}
        {data.syncedAt ? new Date(data.syncedAt).toLocaleString() : 'unknown'}
      </div>
    </div>
  );
}

function ScoreGauge({ score }: { score: number }) {
  const color = scoreColor(score);
  const label = scoreLabel(score);
  // Simple SVG arc: 270° sweep with a colored stroke from 0..score/100.
  const R = 70;
  const C = 2 * Math.PI * R;
  const arcLen = (270 / 360) * C;
  const filled = (score / 100) * arcLen;
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <svg width={180} height={180} viewBox="0 0 200 200">
        <circle
          cx={100}
          cy={100}
          r={R}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth={16}
          strokeDasharray={`${arcLen} ${C}`}
          strokeLinecap="round"
          transform="rotate(135 100 100)"
        />
        <circle
          cx={100}
          cy={100}
          r={R}
          fill="none"
          stroke={color}
          strokeWidth={16}
          strokeDasharray={`${filled} ${C}`}
          strokeLinecap="round"
          transform="rotate(135 100 100)"
        />
        <text
          x={100}
          y={102}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize={48}
          fontWeight={700}
          fill={color}
        >
          {score}
        </text>
        <text
          x={100}
          y={138}
          textAnchor="middle"
          fontSize={13}
          fill="#64748b"
        >
          out of 100
        </text>
      </svg>
      <div style={{ marginTop: 4, fontSize: 14, fontWeight: 600, color }}>{label}</div>
    </div>
  );
}

function Signals({ data }: { data: ReturnType<typeof useGbpHealthQuery>['data'] & object }) {
  const rows: { label: string; value: React.ReactNode; ok: boolean }[] = [
    {
      label: 'Primary category',
      value: data.primaryCategory ?? '—',
      ok: !!data.primaryCategory,
    },
    {
      label: 'Total categories',
      value: data.categoryCount,
      ok: data.categoryCount >= 3,
    },
    { label: 'Phone listed', value: data.hasPhone ? 'Yes' : 'No', ok: data.hasPhone },
    { label: 'Website listed', value: data.hasWebsite ? 'Yes' : 'No', ok: data.hasWebsite },
    { label: 'Hours set', value: data.hasHours ? 'Yes' : 'No', ok: data.hasHours },
    {
      label: 'Description set',
      value: data.hasDescription ? 'Yes' : 'No',
      ok: data.hasDescription,
    },
    {
      label: 'Rating',
      value: data.rating != null ? `${data.rating.toFixed(1)} ★` : '—',
      ok: data.rating != null && data.rating >= 4.5,
    },
    {
      label: 'Review count',
      value: data.reviewCount,
      ok: data.reviewCount >= 50,
    },
    {
      label: 'Most recent review',
      value:
        data.mostRecentReviewDays == null
          ? '—'
          : `${data.mostRecentReviewDays} days ago`,
      ok: data.mostRecentReviewDays != null && data.mostRecentReviewDays <= 60,
    },
    { label: 'Photos', value: data.photoCount, ok: data.photoCount >= 15 },
  ];
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gap: '8px 24px',
        alignContent: 'center',
      }}
    >
      {rows.map((r) => (
        <div
          key={r.label}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '6px 0',
            borderBottom: '1px solid #f1f5f9',
          }}
        >
          <span style={{ fontSize: 13, color: '#475569' }}>
            <span
              style={{
                display: 'inline-block',
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: r.ok ? '#16a34a' : '#cbd5e1',
                marginRight: 8,
              }}
            />
            {r.label}
          </span>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{r.value}</span>
        </div>
      ))}
    </div>
  );
}

function ActionRow({ item }: { item: GbpHealthActionItem }) {
  const colors: Record<GbpHealthActionItem['priority'], string> = {
    high: '#dc2626',
    medium: '#ca8a04',
    low: '#64748b',
  };
  const labels: Record<GbpHealthActionItem['priority'], string> = {
    high: 'HIGH',
    medium: 'MED',
    low: 'LOW',
  };
  return (
    <li
      style={{
        display: 'grid',
        gridTemplateColumns: '60px 1fr',
        gap: 16,
        padding: '14px 20px',
        borderBottom: '1px solid #f1f5f9',
        alignItems: 'flex-start',
      }}
    >
      <span
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: 0.5,
          color: '#fff',
          background: colors[item.priority],
          padding: '4px 8px',
          borderRadius: 4,
          textAlign: 'center',
          alignSelf: 'flex-start',
        }}
      >
        {labels[item.priority]}
      </span>
      <div>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a', marginBottom: 4 }}>
          {item.title}
        </div>
        <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.5 }}>{item.detail}</div>
      </div>
    </li>
  );
}
